import React, { createContext, useState, useEffect, useContext } from "react";
import BusinessContext from "./business-context";
import { refreshToken, withActiveToken } from "../lib/auth";
import { useRouter } from "next/router";

const PegsContext = createContext({
    handleCreatePeg: () => { },
    getPegs: () => { },
    pegs: null,
    deletePeg: () => { },
    getPeg: () => { },
    generateToken: () => { },
    getTokens: () => { },
    deleteToken: () => { },
    checkPegAvalability: () => { },
    onCreatePeg: () => { },
    createdToken: null,
    setCreatedToken: () => { },
    getVersions: () => { },
    getfunctionVersions: () => { },
    activeVersion: null,
    setActiveVersion: () => { },
});

export const PegsProvider = (props) => {
    const router = useRouter();
    const { setNotificationContent } = useContext(BusinessContext);
    const [pegs, setPegs] = useState(null);
    const [activeVersion, setActiveVersion] = useState(null);
    const [createdToken, setCreatedToken] = useState(null);

    const businessUuid = router.query.uuid

    const checkPegAvalability = async (peg, callback, onError) => {
        try {
            const response = await fetch(`https://repo.workpeg.com/api/pegs/?peg_id=${peg}`, {
                headers: {
                    'X-BUSINESS': businessUuid
                }
            })
            if (!response.ok) throw new Error('Failed to check peg availability')
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
            onError();
        }
    }

    const onCreatePeg = async (peg, callback, onError) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(`https://repo.workpeg.com/api/pegs/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id: peg.urn, name: peg.name, description: peg.description })
                })
                if (!response.ok) throw new Error('Failed to create peg')
                const data = await response.json()
                callback(data)
            } catch (error) {
                console.error(error)
                onError();
            }
        }, router)
    }

    const getPegs = async () => {
        try {
            // const response = await fetch(`https://repo.workpeg.com/api/pegs/`, {
                const response = await fetch(`https://repo.workpeg.com/api/pegs/?owner_id=${businessUuid}`, {
                headers: {
                    'X-BUSINESS': businessUuid
                }
            })
            if (!response.ok) throw new Error('Failed to fetch pegs')
            const data = await response.json()
            setPegs(data)
        } catch (error) {
            console.error(error)
        }
    }

    const handleCreatePeg = (peg, callback, onError) => {

        const createPegCallback = (json) => {
            callback(json);
            getPegs();
        }
        refreshToken((token) => { onCreatePeg(peg, createPegCallback, onError) })
    }

    const getPeg = async (pegId, callback) => {
        try {
            const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/`, {
                headers: {
                    'X-BUSINESS': businessUuid
                }
            })
            if (!response.ok) throw new Error('Failed to fetch peg')
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
        }
    }

    const deletePeg = async (pegId, callback) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                    }
                })
                if (!response.ok) throw new Error('Failed to delete peg')
                callback()
                setPegs(pegs.filter(peg => peg.id !== pegId))
            } catch (error) {
                console.error(error)
            }
        }, router)
    }

    const generateToken = async (pegId, tokenTag, callback) => {

        const payload = {
            tag: tokenTag,
            ttl: 31536000,
            peg_id: pegId,
        }

        withActiveToken(async () => {
            try {
                const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/keys/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                })
                if (!response.ok) throw new Error('Failed to generate token')
                const data = await response.json()
                callback(data)
            } catch (error) {
                console.error(error)
            }
        }, router)
    }

    const getTokens = async (pegId, callback, onError) => {
        try {
            const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/keys/`, {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error('Failed to fetch tokens')
            const data = await response.json()
            callback(data)
        } catch (error) {
            onError && onError();
            console.error(error)
        }
    }

    const getfunctionVersions = async (pegId, branchId, callback) => {
        try {
            const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/functions/${branchId}/`, {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error(`Failed to fetch versions for branch ${branchId}`)
            const data = await response.json()
            callback(data)
        } catch (error) {
            console.error(error)
        }

    }

    const getVersions = async (pegId, callback, onError) => {
        try {
            const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/functions/`, {
                method: 'GET',
                headers: {
                    'X-BUSINESS': businessUuid,
                },
                credentials: 'include',
            })
            if (!response.ok) throw new Error('Failed to fetch functions')
            const data = await response.json()
            const functions = data.functions || []
            if (functions.length === 0) {
                onError && onError();
                return;
            }
            await Promise.all(functions.map(fn => getfunctionVersions(pegId, fn, callback, onError)))
        } catch (error) {
            console.error(error)
            onError && onError()
        }
    }

    const deleteToken = async (pegId, tokenId, callback) => {
        withActiveToken(async () => {
            try {
                const response = await fetch(`https://repo.workpeg.com/api/pegs/${pegId}/keys/${tokenId}/`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'X-BUSINESS': businessUuid,
                    }
                })
                if (!response.ok) throw new Error('Failed to delete token')
                callback()
            } catch (error) {
                console.error(error)
            }
        }, router)
    }

    
    const context = {
        handleCreatePeg,
        getPegs,
        pegs,
        deletePeg,
        getPeg,
        generateToken,
        getTokens,
        deleteToken,
        checkPegAvalability,
        onCreatePeg,
        setCreatedToken,
        createdToken,
        getVersions,
        getfunctionVersions,
        activeVersion,
        setActiveVersion,
    };

    return (
        <PegsContext.Provider value={context}>
            {props.children}
        </PegsContext.Provider>
    );
};

export default PegsContext;
